import { useState } from 'react';
import {
  Search,
  Filter,
  X,
  SortAsc,
  SortDesc
} from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { Card, CardContent } from '../ui/Card';
import { NativeSelect } from '../ui/NativeSelect';
import {
  ListFilters as IListFilters,
  ListSort,
  LIST_STATUS_CONFIG
} from '@/types/lists';

interface ListsFiltersProps {
  filters: IListFilters;
  sort: ListSort;
  onFiltersChange: (filters: Partial<IListFilters>) => void;
  onSortChange: (sort: ListSort) => void;
  onClearFilters: () => void;
  availableGoals?: Array<{ id: string; title: string }>;
  availableAspirations?: Array<{ id: string; title: string }>;
  totalCount?: number;
  filteredCount?: number;
  className?: string;
}

export function ListsFilters({
  filters,
  sort,
  onFiltersChange,
  onSortChange,
  onClearFilters,
  availableGoals = [],
  availableAspirations = [],
  totalCount,
  filteredCount,
  className
}: ListsFiltersProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

  const hasActiveFilters = Object.values(filters).some(value => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object' && value !== null) return true;
    return value !== undefined && value !== null && value !== '';
  });

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.status && filters.status.length > 0) {
      count += filters.status.length;
    }
    if (filters.goalId && filters.goalId.length > 0) count += filters.goalId.length;
    if (filters.aspirationId && filters.aspirationId.length > 0) count += filters.aspirationId.length;
    if (filters.hasDeadline !== undefined) count++;
    if (filters.isOverdue) count++;
    return count;
  };

  const sortOptions = [
    { field: 'priority', label: 'Priority' },
    { field: 'title', label: 'Title' },
    { field: 'progress', label: 'Progress' },
    { field: 'deadline', label: 'Deadline' },
    { field: 'updatedAt', label: 'Last Updated' },
    { field: 'createdAt', label: 'Created Date' },
    { field: 'status', label: 'Status' },
  ] as const;

  const handleStatusToggle = (status: keyof typeof LIST_STATUS_CONFIG) => {
    const currentStatuses = filters.status || [];
    const newStatuses = currentStatuses.includes(status)
      ? currentStatuses.filter(s => s !== status)
      : [...currentStatuses, status];

    onFiltersChange({ status: newStatuses.length > 0 ? newStatuses : undefined });
  };

  const handleGoalToggle = (goalId: string) => {
    const currentGoals = filters.goalId || [];
    const newGoals = currentGoals.includes(goalId)
      ? currentGoals.filter(g => g !== goalId)
      : [...currentGoals, goalId];

    onFiltersChange({ goalId: newGoals.length > 0 ? newGoals : undefined });
  };

  const handleAspirationToggle = (aspirationId: string) => {
    const currentAspirations = filters.aspirationId || [];
    const newAspirations = currentAspirations.includes(aspirationId)
      ? currentAspirations.filter(a => a !== aspirationId)
      : [...currentAspirations, aspirationId];

    onFiltersChange({ aspirationId: newAspirations.length > 0 ? newAspirations : undefined });
  };

  const handleSearchChange = (value: string) => {
    onFiltersChange({ search: value || undefined });
  };

  // Build summary text - always show count format like Aspirations and Goals
  const getSummaryText = () => {
    const total = totalCount ?? 0;
    const filtered = filteredCount ?? total;
    return `Showing ${filtered} of ${total} lists`;
  };

  return (
    <Card className={className}>
      <CardContent className="filters-content">
        {/* Mobile: Compact toggle button */}
        <div className="filters-mobile">
          <div className="layout-row-between">
            <span className="text-muted-sm">
              {getSummaryText()}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsMobileFiltersOpen(!isMobileFiltersOpen)}
              className="layout-row-gap"
            >
              <Filter className="icon-md" />
              Filters & Sort
              {hasActiveFilters && (
                <Badge variant="secondary" className="ml-1">
                  {getActiveFilterCount()}
                </Badge>
              )}
            </Button>
          </div>
        </div>

        {/* Desktop: Always visible controls */}
        <div className="filters-desktop">
          {/* Search and Sort Row */}
          <div className="filters-row">
            {/* Search */}
            <div className="filters-search-container">
              <Search className="filters-search-icon" />
              <Input
                placeholder="Search lists..."
                value={filters.search || ''}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 pr-8"
              />
              {filters.search && (
                <button
                  type="button"
                  onClick={() => handleSearchChange('')}
                  className="clear-btn"
                  aria-label="Clear search"
                >
                  <X className="h-6 w-6 text-muted-foreground" />
                </button>
              )}
            </div>

            {/* Sort */}
            <div className="layout-row-gap">
              <NativeSelect
                value={sort.field}
                onChange={(e) => onSortChange({ field: e.target.value as ListSort['field'], direction: sort.direction })}
              >
                {sortOptions.map(option => (
                  <option key={option.field} value={option.field}>
                    Sort by {option.label}
                  </option>
                ))}
              </NativeSelect>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSortChange({ ...sort, direction: sort.direction === 'asc' ? 'desc' : 'asc' })}
              >
                {sort.direction === 'asc' ? <SortAsc className="icon-md" /> : <SortDesc className="icon-md" />}
              </Button>
            </div>

            {/* Filter Toggle */}
            <Button
              variant="outline"
              onClick={() => setIsExpanded(!isExpanded)}
              className="layout-row-gap"
            >
              <Filter className="icon-md" />
              Filters
              {hasActiveFilters && (
                <Badge variant="secondary" className="ml-1">
                  {getActiveFilterCount()}
                </Badge>
              )}
            </Button>
          </div>

          {/* Results Summary */}
          <div className="filters-summary">
            <span>
              {getSummaryText()}
            </span>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearFilters}
                className="text-xs"
              >
                <X className="icon-sm mr-1" />
                Clear filters
              </Button>
            )}
          </div>

          {/* Desktop Expanded Filters */}
          {isExpanded && (
            <div className="filters-expanded">
              {/* Statuses */}
              <div className="filters-section">
                <label className="text-label">Status</label>
                <div className="filters-buttons">
                  {Object.entries(LIST_STATUS_CONFIG)
                    .filter(([status]) => status !== 'template')
                    .map(([status, config]) => (
                      <button
                        key={status}
                        onClick={() => handleStatusToggle(status as keyof typeof LIST_STATUS_CONFIG)}
                        className={`filters-btn ${
                          filters.status?.includes(status as keyof typeof LIST_STATUS_CONFIG) ? 'filters-btn-active' : ''
                        }`}
                      >
                        {config.label}
                      </button>
                    ))}
                </div>
              </div>

              {/* Linked Aspirations */}
              {availableAspirations.length > 0 && (
                <div className="filters-section">
                  <label className="text-label">Linked to Aspiration</label>
                  <div className="filters-buttons">
                    {availableAspirations.map(aspiration => (
                      <button
                        key={aspiration.id}
                        onClick={() => handleAspirationToggle(aspiration.id)}
                        className={`filters-btn ${
                          filters.aspirationId?.includes(aspiration.id) ? 'filters-btn-active' : ''
                        }`}
                      >
                        {aspiration.title}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Linked Goals */}
              {availableGoals.length > 0 && (
                <div className="filters-section">
                  <label className="text-label">Linked to Goal</label>
                  <div className="filters-buttons">
                    {availableGoals.map(goal => (
                      <button
                        key={goal.id}
                        onClick={() => handleGoalToggle(goal.id)}
                        className={`filters-btn ${
                          filters.goalId?.includes(goal.id) ? 'filters-btn-active' : ''
                        }`}
                      >
                        {goal.title}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Filters */}
              <div className="filters-section">
                <label className="text-label">Quick Filters</label>
                <div className="filters-buttons">
                  <button
                    onClick={() => onFiltersChange({ isOverdue: filters.isOverdue ? undefined : true })}
                    className={`filters-btn ${
                      filters.isOverdue ? 'filters-btn-active filters-btn-danger' : ''
                    }`}
                  >
                    Overdue
                  </button>
                  <button
                    onClick={() => onFiltersChange({ hasDeadline: filters.hasDeadline ? undefined : true })}
                    className={`filters-btn ${
                      filters.hasDeadline ? 'filters-btn-active' : ''
                    }`}
                  >
                    Has Deadline
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mobile: Expanded filters */}
        {isMobileFiltersOpen && (
          <div className="filters-mobile filters-expanded">
            {/* Search */}
            <div className="filters-search-container">
              <Search className="filters-search-icon" />
              <Input
                placeholder="Search lists..."
                value={filters.search || ''}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 pr-8"
              />
              {filters.search && (
                <button
                  type="button"
                  onClick={() => handleSearchChange('')}
                  className="clear-btn"
                  aria-label="Clear search"
                >
                  <X className="h-6 w-6 text-muted-foreground" />
                </button>
              )}
            </div>

            {/* Sort */}
            <div className="filters-section">
              <label className="text-label">Sort</label>
              <div className="layout-row-gap">
                <NativeSelect
                  value={sort.field}
                  onChange={(e) => onSortChange({ field: e.target.value as ListSort['field'], direction: sort.direction })}
                  className="flex-1"
                >
                  {sortOptions.map(option => (
                    <option key={option.field} value={option.field}>
                      Sort by {option.label}
                    </option>
                  ))}
                </NativeSelect>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onSortChange({ ...sort, direction: sort.direction === 'asc' ? 'desc' : 'asc' })}
                >
                  {sort.direction === 'asc' ? <SortAsc className="icon-md" /> : <SortDesc className="icon-md" />}
                </Button>
              </div>
            </div>

            {/* Statuses */}
            <div className="filters-section">
              <label className="text-label">Status</label>
              <div className="filters-buttons">
                {Object.entries(LIST_STATUS_CONFIG)
                  .filter(([status]) => status !== 'template')
                  .map(([status, config]) => (
                    <button
                      key={status}
                      onClick={() => handleStatusToggle(status as keyof typeof LIST_STATUS_CONFIG)}
                      className={`filters-btn ${
                        filters.status?.includes(status as keyof typeof LIST_STATUS_CONFIG) ? 'filters-btn-active' : ''
                      }`}
                    >
                      {config.label}
                    </button>
                  ))}
              </div>
            </div>

            {/* Quick Filters */}
            <div className="filters-section">
              <label className="text-label">Quick Filters</label>
              <div className="filters-buttons">
                <button
                  onClick={() => onFiltersChange({ isOverdue: filters.isOverdue ? undefined : true })}
                  className={`filters-btn ${
                    filters.isOverdue ? 'filters-btn-active filters-btn-danger' : ''
                  }`}
                >
                  Overdue
                </button>
                <button
                  onClick={() => onFiltersChange({ hasDeadline: filters.hasDeadline ? undefined : true })}
                  className={`filters-btn ${
                    filters.hasDeadline ? 'filters-btn-active' : ''
                  }`}
                >
                  Has Deadline
                </button>
              </div>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <div className="pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClearFilters}
                  className="text-xs"
                >
                  <X className="icon-sm mr-1" />
                  Clear all filters
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
